import { SxProps, Theme } from '@mui/material/styles';
import { columnFlex, overflowEllipsis } from '../../lib/styles/commonStyles';

/** COLORS AND STYLES */

export const outlinedButtonColor = '#134085';
export const darkBackgroundColor = '#053570';
export const chipHoverBackgroundColor = '#B1D7F6';
export const chipBackgroundColor = '#C9E3F9';
export const commonTypographyStyle: SxProps = {
  fontWeight: 500,
  lineHeight: '22px',
  marginRight: '8px',
  marginLeft: '12px',
};

export const selectButtonLabel: SxProps = { ...columnFlex, gap: '4px' };

export const sliderOuterBoxStyling: SxProps = { ...columnFlex, padding: '0px 16px 8px 16px' };
export const sliderInnerBoxStyling: SxProps = { padding: '0px 4px', marginTop: '16px' };

/** FUNCTIONS */
export const defaultOutlineButtonStyling = (theme: Theme) => ({
  backgroundColor: theme.palette.background.default,
  color: theme.palette.primary.main,
  '& .MuiButton-startIcon': { marginRight: '0px' },
});

export const drawerSelectButtonStyling = (anchorEl: HTMLInputElement | HTMLButtonElement | null, theme: Theme) => {
  const commonStyle = {
    height: '44px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    justifyContent: 'space-between',
  };

  if (anchorEl) {
    return {
      ...commonStyle,
      border: `2px solid ${theme.palette.primary.main}`,
      '&:hover, &:focus:hover': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `2px solid ${theme.palette.primary.main}`,
      },
    };
  }
  return {
    ...commonStyle,
    border: `1px solid ${theme.palette.text.secondary}`,
    '&:hover, &:focus:hover': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.text.secondary}`,
    },
  };
};

export const defaultButtonStyling = (anchorEl: HTMLInputElement | HTMLButtonElement | null, theme: Theme) => {
  if (theme.palette.mode === 'dark') {
    return {
      backgroundColor: theme.palette.grey[800],
      color: theme.palette.text.primary,
    };
  }
  if (anchorEl) {
    return {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.text.primary,
      '& .MuiButton-startIcon': { marginRight: '0px' },
    };
  }
  return {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    '&:focus-visible': {
      boxShadow: 'none',
      boxSizing: 'border-box',
      outline: `2px solid ${outlinedButtonColor}`,
    },
    '&:hover, &:focus:hover': {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.text.primary,
    },
    '&:focus': {
      color: theme.palette.text.primary,
    },
    '& .MuiButton-startIcon': { marginRight: '0px' },
  };
};

export const selectedButtonStyling = (anchorEl: HTMLInputElement | HTMLButtonElement | null, theme: Theme) => {
  if (theme.palette.mode === 'dark') {
    return {
      backgroundColor: darkBackgroundColor,
      color: theme.palette.text.primary,
    };
  }
  if (anchorEl) {
    return {
      backgroundColor: chipHoverBackgroundColor,
      color: darkBackgroundColor,
      '& .MuiButton-startIcon': { marginRight: '0px' },
    };
  }
  return {
    backgroundColor: chipBackgroundColor,
    color: darkBackgroundColor,
    '&:focus': {
      color: darkBackgroundColor,
    },
    '&:hover, &:focus:hover': {
      backgroundColor: chipHoverBackgroundColor,
      color: darkBackgroundColor,
    },
    '&:focus-visible': {
      boxShadow: 'none',
      boxSizing: 'border-box',
      outline: `2px solid ${outlinedButtonColor}`,
    },
    '& .MuiButton-startIcon': { marginRight: '0px' },
  };
};

export const dropDownSelectButtonStyling = (anchorEl: HTMLInputElement | HTMLButtonElement | null, theme: Theme) => {
  const commonStyle = {
    height: '32px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    justifyContent: 'space-between',
  };

  if (anchorEl) {
    return {
      ...commonStyle,
      '&:hover, &:focus:hover': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      },
    };
  }
  return {
    ...commonStyle,
    borderBottom: `1px solid ${theme.palette.text.secondary}`,
    borderRadius: 0,
    padding: '0px',
    font: 'inherit',
    color: `${theme.palette.text.primary} !important`,
    '&:hover, &:focus:hover, &:focus-visible': {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.text.secondary}`,
      borderRadius: 0,
      padding: '0px',
      font: 'inherit',
    },
  };
};

export const getMinWidth = (isDrawerSelect?: boolean, isDropDownSelect?: boolean) => {
  if (isDrawerSelect) {
    return '300px';
  }
  if (isDropDownSelect) {
    return '320px';
  }
  return '360px';
};

export const definedFilterStyling = (theme: Theme) => ({
  '&:focus-visible': {
    boxShadow: 'none',
    boxSizing: 'border-box',
    outline: `2px solid ${theme.palette.primary.dark}`,
    outlineOffset: '-2px',
    // This converts the hoverOpacity (default 0.04) to hexadecimal then grabs the first two values past the decimal to append
    // to the main theme hex. These two values represent opacity when they're appended to a 6 digit hex color value
    backgroundColor: `${theme.palette.primary.main}${theme.palette.action.hoverOpacity.toString(16).substring(2, 4)}`,
    '& .MuiButton-startIcon': { marginRight: '0px' },
  },
});

export const searchBarBoxStyling = (theme: Theme, isDarkMode: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: isDarkMode ? theme.palette.grey[800] : '#F5F5F5',
  height: '48px',
  borderRadius: 1,
  paddingLeft: '12px',
  paddingRight: '8px',
  marginLeft: '16px',
  marginRight: '16px',
});

export const tabBarStyling = (isDrawerSelect: boolean | undefined) => ({
  marginRight: '16px',
  marginLeft: '16px',
  maxWidth: isDrawerSelect ? '300px' : '100%',
  ...overflowEllipsis,
});

export const singleSelectButtonStyling = (isDrawerSelect?: boolean): SxProps =>
  isDrawerSelect
    ? {
        paddingRight: '4px',
        paddingLeft: '5px',
        maxWidth: '220px',
        textAlign: 'left',
        ...overflowEllipsis,
      }
    : {
        fontWeight: 500,
        lineHeight: '22px',
        marginRight: '4px',
        marginLeft: '12px',
      };

export const singleSelectButtonFontSize = (isDrawerSelect?: boolean) => (isDrawerSelect ? 'medium' : 'inherit');

export const filterContainerStyling: SxProps = { gap: '8px', alignContent: 'center', alignItems: 'center' };

export const arrowStyling = (theme: Theme, predefined: boolean) => ({
  color: predefined ? theme.palette.primary.main : theme.palette.grey[600],
  marginLeft: '4px',
  marginRight: '4px',
});

export const getDarkModeButtonSx = (isDarkModeActive: boolean, theme: Theme) => {
  if (isDarkModeActive) {
    return {
      backgroundColor: darkBackgroundColor,
      color: theme.palette.text.primary,
    };
  }
  return {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.text.primary,
  };
};

export const getErrorButtonSx = (
  anchorEl: HTMLInputElement | HTMLButtonElement | null,
  theme: Theme,
  isDrawerSelect?: boolean,
  isDropDownSelect?: boolean
) => {
  let baseStyle;
  if (isDrawerSelect) {
    baseStyle = drawerSelectButtonStyling(anchorEl, theme);
  } else if (isDropDownSelect) {
    baseStyle = dropDownSelectButtonStyling(anchorEl, theme);
  } else {
    baseStyle = defaultButtonStyling(anchorEl, theme);
  }

  return {
    ...baseStyle,
    borderColor: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.dark,
    },
  };
};

export const dateFilterBoxSx = {
  display: 'flex',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
  paddingLeft: '34px', // 18px from the calendar + 5px from the chip margin + 16px from the padding (-5px from the chip margin)
};

export const dateFilterRangePickerSx = { width: '100%', alignItems: 'center', paddingLeft: '18px' };

export const dateFilterChipSx = {
  backgroundColor: chipBackgroundColor,
  margin: '5px',
  color: darkBackgroundColor,
  '&:hover': {
    backgroundColor: chipBackgroundColor,
    color: darkBackgroundColor,
  },
};

export const dateFilterButtonBadgeSx = {
  '& .MuiBadge-badge': {
    right: 3,
    top: 4,
    border: '0px',
    width: '12px',
    height: '12px',
  },
};

export const dateFilterButtonDarkSx = (theme: Theme) => ({
  backgroundColor: darkBackgroundColor,
  color: theme.palette.text.primary,
  '& .MuiButton-startIcon': { marginRight: '0px' },
});

export const dateFilterButtonLightSx = (theme: Theme) => ({
  backgroundColor: theme.palette.grey[800],
  color: theme.palette.text.primary,
  '& .MuiButton-startIcon': { marginRight: '0px' },
});

export const dateFilterBtnRangeIconSx = (theme: Theme) => ({
  color: theme.palette.primary.main,
  fontSize: 'medium',
  marginLeft: '6px',
  marginRight: '2px',
});

export const dateFilterBtnResetSx = (predefined: boolean | undefined, dateButtonStyle: SxProps<Theme> | undefined) => ({
  marginRight: `${predefined ? '4px' : '0px'}`,
  marginLeft: `${dateButtonStyle ? '0px' : '4px'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: dateButtonStyle ? 400 : 500,
  lineHeight: '22px',
});

export const dateFilterBtnCalendarSx = (theme: Theme) => ({
  color: theme.palette.text.secondary,
});

export default {
  defaultButtonStyling,
  selectedButtonStyling,
  dateFilterBoxSx,
  dateFilterRangePickerSx,
  dateFilterChipSx,
};
